import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Reservation, Member, ReservationBookedRv, ReservationBookedPv } from '../../types';
import Modal from '../shared/Modal';
import { formatDate, formatDateRange } from '../../utils/dateUtils';
import { formatCurrency, formatStatus, formatPhone, getStatusColor, getPaymentStatusColor } from '../../utils/formatters';
import { fetchPaymentsForReservation, PaymentRecord } from '../../services/paymentService';
import { authService } from '../../services/auth';
import { useModificationHistory } from '../../services/useModificationHistory';
import { parseSpecialRequests } from '../../services/reservationService';
import {
  getRecreationalVehicles,
  createRecreationalVehicle,
  updateRecreationalVehicle,
  ApiRecreationalVehicle,
  RecreationalVehicleCreate,
} from '../../services/memberService';
import ModificationHistorySection from './ModificationHistorySection';
import ReservationReviewSection from './ReservationReviewSection';
import PaymentLinkSection from './PaymentLinkSection';
import {
  PaymentAction,
  PaymentStatusActionButtons,
  PaymentStatusConfirmModal,
  getPaymentActions,
  statusUpdateResultText,
} from './PaymentStatusUpdate';
import './ReservationPopup.css';

// The booking backend stores all rv values as strings; amp "0" + electric
// "false" means no electrical. Build a compact human-readable summary.
const formatBookedRv = (rv: ReservationBookedRv): string => {
  const parts: string[] = [];
  if (rv.type) parts.push(rv.type);
  if (rv.description) parts.push(rv.description);
  if (rv.length && rv.length !== '0') parts.push(`${rv.length}ft`);
  const wantsElectric = rv.electric === 'true' || (!!rv.amp && rv.amp !== '0');
  if (!wantsElectric) {
    parts.push('No electric');
  } else if (rv.amp && rv.amp !== '0') {
    parts.push(`${rv.amp}A`);
  }
  if (rv.slideouts && rv.slideouts !== '0') {
    parts.push(`${rv.slideouts} slide-out${rv.slideouts === '1' ? '' : 's'}`);
  }
  if (rv.license_plate) parts.push(rv.license_plate);
  return parts.join(' · ');
};

const formatBookedPv = (pv: ReservationBookedPv): string => {
  const desc = [pv.color, pv.make, pv.model].filter(Boolean).join(' ');
  const parts: string[] = [];
  if (desc) parts.push(desc);
  if (pv.license_plate) parts.push(pv.license_plate);
  return parts.join(' · ') || 'Vehicle';
};

// Map a reservation's booked RV (string-valued) into a member-profile RV payload.
// amp "0" (no electrical) collapses to undefined; blank strings drop out.
const bookedRvToPayload = (rv: ReservationBookedRv): RecreationalVehicleCreate => {
  const length = rv.length && rv.length !== '0' ? parseFloat(rv.length) : undefined;
  const amp = rv.amp && rv.amp !== '0' ? parseInt(rv.amp, 10) : undefined;
  return {
    type: (rv.type || '').trim(),
    description: rv.description?.trim() || undefined,
    length: Number.isFinite(length as number) ? length : undefined,
    amp: Number.isFinite(amp as number) ? amp : undefined,
    license_plate: rv.license_plate?.trim() || undefined,
  };
};

// Does an existing member-profile RV already represent this booked RV?
// Compared on the meaningful, round-trippable fields so a match after
// auto-save stays a match on the next open (no duplicate creation).
const normRv = (v: unknown): string => (v ?? '').toString().trim().toLowerCase();
const numEqRv = (a?: number, b?: number): boolean =>
  (a == null && b == null) || (a != null && b != null && a === b);

const memberRvMatchesBooked = (
  existing: ApiRecreationalVehicle,
  candidate: RecreationalVehicleCreate
): boolean =>
  normRv(existing.type) === normRv(candidate.type) &&
  normRv(existing.description) === normRv(candidate.description) &&
  normRv(existing.license_plate) === normRv(candidate.license_plate) &&
  numEqRv(existing.length, candidate.length) &&
  numEqRv(existing.amp, candidate.amp);

interface ReservationPopupProps {
  reservation: Reservation;
  member: Member | null;
  onClose: () => void;
  onViewMember: (memberId: number) => void;
  onViewPayment: () => void;
  onModify: () => void;
  onCancel: () => void;
  onResendAgreement: () => void;
  onCheckIn: () => void;
  onCheckOut: () => void;
  onUndoCheckIn: (index: number) => void;
}

const ReservationPopup: React.FC<ReservationPopupProps> = ({
  reservation,
  member,
  onClose,
  onViewMember,
  onViewPayment,
  onModify,
  onCancel,
  onResendAgreement,
  onCheckIn,
  onCheckOut,
  onUndoCheckIn
}) => {
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [paymentLoading, setPaymentLoading] = useState(true);
  const [pendingAction, setPendingAction] = useState<{ payment: PaymentRecord; action: PaymentAction } | null>(null);
  const [paymentResult, setPaymentResult] = useState<{ text: string; noOp: boolean } | null>(null);
  const [rvRecords, setRvRecords] = useState<ApiRecreationalVehicle[]>([]);
  const [rvFormOpen, setRvFormOpen] = useState(false);
  const [editingRv, setEditingRv] = useState<ApiRecreationalVehicle | null>(null);
  const [rvFormData, setRvFormData] = useState({ type: '', description: '', length: '', amp: '', licensePlate: '' });
  const [rvSaving, setRvSaving] = useState(false);
  const [rvError, setRvError] = useState<string | null>(null);

  // Guards the auto-save from firing more than once per reservation (incl.
  // React StrictMode's double effect invocation in dev).
  const autoSyncedRef = useRef<number | null>(null);

  useEffect(() => {
    const memberId = reservation.memberId;
    if (!memberId) return;
    let cancelled = false;

    getRecreationalVehicles(memberId)
      .then(async rvs => {
        const active = rvs.filter(rv => rv.active);
        if (cancelled) return;
        setRvRecords(active);

        // Auto-save this reservation's booked RV into the member's profile,
        // but only when it differs from every RV already on file (a genuine
        // change) — otherwise leave the profile alone.
        const booked = reservation.bookedRv;
        if (!booked || !booked.type || autoSyncedRef.current === reservation.id) return;
        const candidate = bookedRvToPayload(booked);
        if (active.some(rv => memberRvMatchesBooked(rv, candidate))) return;

        autoSyncedRef.current = reservation.id;
        try {
          const created = await createRecreationalVehicle(memberId, candidate);
          if (!cancelled) setRvRecords(prev => [...prev, created]);
        } catch {
          // Non-blocking: auto-save is a convenience, never surface an error here.
          autoSyncedRef.current = null;
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [reservation.memberId, reservation.id, reservation.bookedRv]);

  const openAddRvForm = () => {
    setEditingRv(null);
    setRvFormData({ type: '', description: '', length: '', amp: '', licensePlate: '' });
    setRvError(null);
    setRvFormOpen(true);
  };

  const openEditRvForm = (rv: ApiRecreationalVehicle) => {
    setEditingRv(rv);
    setRvFormData({
      type: rv.type,
      description: rv.description || '',
      length: rv.length?.toString() || '',
      amp: rv.amp?.toString() || '',
      licensePlate: rv.license_plate || '',
    });
    setRvError(null);
    setRvFormOpen(true);
  };

  const saveRv = async () => {
    if (!rvFormData.type.trim()) { setRvError('Type is required'); return; }
    setRvSaving(true);
    setRvError(null);
    try {
      const payload = {
        type: rvFormData.type.trim(),
        description: rvFormData.description.trim() || undefined,
        length: rvFormData.length ? parseFloat(rvFormData.length) : undefined,
        amp: rvFormData.amp ? parseInt(rvFormData.amp) : undefined,
        license_plate: rvFormData.licensePlate.trim() || undefined,
      };
      if (editingRv) {
        const updated = await updateRecreationalVehicle(editingRv.id, payload);
        setRvRecords(prev => prev.map(r => r.id === updated.id ? updated : r));
      } else {
        const created = await createRecreationalVehicle(reservation.memberId!, payload);
        setRvRecords(prev => [...prev, created]);
      }
      setRvFormOpen(false);
      setEditingRv(null);
    } catch (err: any) {
      setRvError(err.message || 'Failed to save RV');
    } finally {
      setRvSaving(false);
    }
  };

  const loadPayments = useCallback(() => {
    const storeId = authService.getStoreId();
    if (!storeId) {
      setPaymentLoading(false);
      return;
    }
    const fetches = [fetchPaymentsForReservation(reservation.id, storeId)];
    if (reservation.originalReservationId) {
      fetches.push(fetchPaymentsForReservation(reservation.originalReservationId, storeId));
    }
    Promise.all(fetches)
      .then((results) => {
        const merged = results.flat();
        const unique = Array.from(new Map(merged.map((p) => [p.id, p])).values());
        setPayments(unique);
      })
      .catch((err) => console.error('Payment fetch error:', err))
      .finally(() => setPaymentLoading(false));
  }, [reservation.id, reservation.originalReservationId]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const modHistory = useModificationHistory(reservation, payments);

  // Parse special requests for check-in/check-out data
  const parsedData = parseSpecialRequests(reservation.specialRequests);

  const paymentStatus = payments.length > 0 ? payments[0].status : null;
  const latestPayment = payments.length > 0 ? payments[0] : null;
  const totalPaid = payments
    .filter((p) => p.status === 'completed')
    .reduce((sum, p) => sum + p.amount, 0);

  const footer = (
    <>
      <button className="btn btn-secondary" onClick={onClose}>
        OK
      </button>
      {/* Check-In button - available for confirmed reservations */}
      {reservation.status === 'confirmed' && (
        <button className="btn btn-blue" onClick={onCheckIn}>
          CHECK IN
        </button>
      )}
      {/* Check-Out button - available after check-ins exist */}
      {reservation.status === 'confirmed' && parsedData.check_ins && parsedData.check_ins.length > 0 && (
        <button className="btn btn-blue" onClick={onCheckOut}>
          CHECK OUT
        </button>
      )}
      <button className="btn btn-warning" onClick={onModify}>
        MODIFY RESERVATION
      </button>
      <button className="btn btn-primary" onClick={onResendAgreement}>
        REISSUE CONFIRMATION
      </button>
      <button className="btn btn-danger" onClick={onCancel}>
        CANCELLATION
      </button>
    </>
  );

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Reservation ${reservation.confirmationCode}`}
      size="large"
      footer={footer}
    >
      <div className="reservation-popup">
        {/* Status Badge */}
        <div className="popup-status">
          <span
            className="status-badge"
            style={{ backgroundColor: getStatusColor(reservation.status) }}
          >
            {formatStatus(reservation.status)}
          </span>
        </div>

        {/* Booking Information */}
        <section className="popup-section">
          <h3 className="section-title">Booking Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Dates</label>
              <span>{formatDateRange(reservation.checkIn + 'T12:00:00', reservation.checkOut + 'T12:00:00')}</span>
            </div>
            <div className="info-item">
              <label>Nights</label>
              <span>{reservation.days}</span>
            </div>
            <div className="info-item">
              <label>Site</label>
              <span>{reservation.siteAlias}</span>
            </div>
            <div className="info-item">
              <label>Confirmation</label>
              <span>{reservation.confirmationCode}</span>
            </div>
            <div className="info-item">
              <label>Adults</label>
              <span>{reservation.adults}</span>
            </div>
            <div className="info-item">
              <label>Children</label>
              <span>{reservation.children}</span>
            </div>
            <div className="info-item">
              <label>Pets</label>
              <span>{reservation.pets}</span>
            </div>
            <div className="info-item">
              <label>Vehicles</label>
              <span>{reservation.vehicles}</span>
            </div>
          </div>
          <div className="rv-info">
            <div className="rv-info__header">
              <label>RV / Vehicle Details</label>
            </div>

            {/* Booked on this reservation (reservation-scoped — present for
                guest bookings too). This is the RV/vehicle the guest actually
                entered at checkout for this stay. */}
            <div className="rv-booked">
              <div className="rv-booked__label">Booked on this reservation</div>
              {(reservation.bookedRv && reservation.bookedRv.type) ||
              (reservation.bookedPv && reservation.bookedPv.length > 0) ? (
                <div className="rv-list">
                  {reservation.bookedRv && reservation.bookedRv.type && (
                    <div className="rv-entry">
                      <span className="rv-tag">{formatBookedRv(reservation.bookedRv)}</span>
                    </div>
                  )}
                  {reservation.bookedPv?.map((pv, i) => (
                    <div key={i} className="rv-entry">
                      <span className="rv-tag rv-tag--pv">{formatBookedPv(pv)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="rv-empty">No RV/vehicle info booked on this reservation</span>
              )}
            </div>

            {/* Member profile RVs (secondary) — saved to the member's account,
                not specific to this reservation. Editable here. */}
            <div className="rv-profile">
              <div className="rv-info__header rv-info__header--sub">
                <label>Member Profile RVs</label>
                {reservation.memberId && !rvFormOpen && (
                  <button className="rv-add-btn" onClick={openAddRvForm}>+ Add RV</button>
                )}
              </div>
            {!reservation.memberId ? (
              <span className="rv-empty">N/A (guest booking — no member profile)</span>
            ) : rvRecords.length === 0 && !rvFormOpen ? (
              <span className="rv-empty">No RV information on file</span>
            ) : (
              <div className="rv-list">
                {rvRecords.map(rv => {
                  const parts = [rv.type];
                  if (rv.description) parts.push(rv.description);
                  if (rv.length) parts.push(`${rv.length}ft`);
                  if (rv.amp) parts.push(`${rv.amp}A`);
                  if (rv.license_plate) parts.push(rv.license_plate);
                  return (
                    <div key={rv.id} className="rv-entry">
                      <span className="rv-tag">{parts.join(' · ')}</span>
                      <button className="rv-edit-btn" onClick={() => openEditRvForm(rv)}>Edit</button>
                    </div>
                  );
                })}
              </div>
            )}
            {rvFormOpen && (
              <div className="rv-form">
                <div className="rv-form__fields">
                  <div className="rv-form__field rv-form__field--full">
                    <label>Type *</label>
                    <input
                      type="text"
                      placeholder="e.g. Class A, Travel Trailer, 5th Wheel"
                      value={rvFormData.type}
                      onChange={e => setRvFormData(f => ({ ...f, type: e.target.value }))}
                      disabled={rvSaving}
                    />
                  </div>
                  <div className="rv-form__field rv-form__field--full">
                    <label>Description</label>
                    <input
                      type="text"
                      placeholder="e.g. White Winnebago"
                      value={rvFormData.description}
                      onChange={e => setRvFormData(f => ({ ...f, description: e.target.value }))}
                      disabled={rvSaving}
                    />
                  </div>
                  <div className="rv-form__field">
                    <label>Length (ft)</label>
                    <input
                      type="number"
                      placeholder="35"
                      value={rvFormData.length}
                      onChange={e => setRvFormData(f => ({ ...f, length: e.target.value }))}
                      disabled={rvSaving}
                    />
                  </div>
                  <div className="rv-form__field">
                    <label>Amp</label>
                    <input
                      type="number"
                      placeholder="30"
                      value={rvFormData.amp}
                      onChange={e => setRvFormData(f => ({ ...f, amp: e.target.value }))}
                      disabled={rvSaving}
                    />
                  </div>
                  <div className="rv-form__field rv-form__field--full">
                    <label>License Plate</label>
                    <input
                      type="text"
                      placeholder="CA-123ABC"
                      value={rvFormData.licensePlate}
                      onChange={e => setRvFormData(f => ({ ...f, licensePlate: e.target.value }))}
                      disabled={rvSaving}
                    />
                  </div>
                </div>
                {rvError && <p className="rv-form__error">{rvError}</p>}
                <div className="rv-form__actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => { setRvFormOpen(false); setEditingRv(null); }}
                    disabled={rvSaving}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-blue btn-sm" onClick={saveRv} disabled={rvSaving}>
                    {rvSaving ? 'Saving...' : editingRv ? 'Save Changes' : 'Add RV'}
                  </button>
                </div>
              </div>
            )}
            </div>
          </div>
          {/* Guest Requests (original text from booking) */}
          {parsedData.guest_requests && (
            <div className="special-requests">
              <label>Special Requests</label>
              <p>{parsedData.guest_requests}</p>
            </div>
          )}

          {/* Flagged indicator */}
          {parsedData.flagged && (
            <div className="flagged-indicator">
              <span className="flagged-badge">★ Flagged</span>
              <span className="flagged-note">This reservation requires special attention</span>
            </div>
          )}

          {/* Check-In History */}
          {parsedData.check_ins && parsedData.check_ins.length > 0 && (
            <div className="check-history">
              <label>Check-Ins</label>
              {parsedData.check_ins.map((entry, i) => (
                <div key={i} className="check-entry">
                  <span className="check-time">{entry.time}</span>
                  {entry.notes && <span className="check-notes">- {entry.notes}</span>}
                  <button
                    className="check-undo-btn"
                    onClick={() => onUndoCheckIn(i)}
                    title="Undo this check-in"
                  >
                    UNDO
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Check-Out History */}
          {parsedData.check_outs && parsedData.check_outs.length > 0 && (
            <div className="check-history">
              <label>Check-Outs</label>
              {parsedData.check_outs.map((entry, i) => (
                <div key={i} className="check-entry">
                  <span className="check-time">{entry.time}</span>
                  {entry.notes && <span className="check-notes">- {entry.notes}</span>}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Guest/Member Information */}
        <section className="popup-section">
          <h3 className="section-title">
            {member ? 'Member Information' : 'Guest Information'}
          </h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Name</label>
              {member ? (
                <button
                  className="member-link"
                  onClick={() => onViewMember(member.id)}
                >
                  {member.firstName} {member.lastName} →
                </button>
              ) : (
                <span>{reservation.guestName}</span>
              )}
            </div>
            <div className="info-item">
              <label>Email</label>
              <span>{member?.email || reservation.guestEmail}</span>
            </div>
            <div className="info-item">
              <label>Phone</label>
              <span>{formatPhone(member?.phone || reservation.guestPhone)}</span>
            </div>
            {reservation.otherGuests && (
              <div className="info-item info-item--full">
                <label>Other Guests</label>
                <span>{reservation.otherGuests}</span>
              </div>
            )}
            {member && (
              <>
                <div className="info-item">
                  <label>Joined</label>
                  <span>{member.createdAt ? formatDate(member.createdAt) : '-'}</span>
                </div>
                <div className="info-item">
                  <label>Last Login</label>
                  <span>{member.lastLoginDate ? formatDate(member.lastLoginDate) : 'Never'}</span>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Payment Information */}
        <section className="popup-section">
          <h3 className="section-title">Payment Information</h3>
          {paymentLoading ? (
            <p className="payment-loading">Loading payment data...</p>
          ) : (
            <div className="payment-summary">
              <div className="payment-row">
                <span>Total Cost</span>
                <span className="payment-amount">{formatCurrency(reservation.totalCost)}</span>
              </div>
              <div className="payment-row">
                <span>Amount Paid</span>
                <span className="payment-amount">{formatCurrency(totalPaid)}</span>
              </div>
              <div className="payment-row">
                <span>Balance Due</span>
                <span className="payment-amount payment-amount--due">
                  {formatCurrency(Math.max(0, reservation.totalCost - totalPaid))}
                </span>
              </div>
              <div className="payment-row">
                <span>Payment Status</span>
                <span
                  className="payment-status"
                  style={{ color: getPaymentStatusColor(paymentStatus || 'pending') }}
                >
                  {paymentStatus ? formatStatus(paymentStatus) : 'No Payments'}
                </span>
              </div>
              {latestPayment?.payment_method && (
                <div className="payment-row">
                  <span>Payment Method</span>
                  <span>
                    {formatStatus(latestPayment.payment_method)}
                    {latestPayment.card_brand && latestPayment.last_four_digits
                      ? ` (${latestPayment.card_brand} ****${latestPayment.last_four_digits})`
                      : ''}
                  </span>
                </div>
              )}
              {payments
                .filter((p) => getPaymentActions(p, reservation.status).actions.length > 0)
                .map((p) => (
                  <div key={p.id} className="payment-row payment-row--actions">
                    <span>
                      {formatCurrency(p.amount)}
                      {p.payment_method ? ` ${formatStatus(p.payment_method)}` : ''}
                      {' '}({formatStatus(p.status)})
                    </span>
                    <PaymentStatusActionButtons
                      payment={p}
                      reservationStatus={reservation.status}
                      onAction={(payment, action) => setPendingAction({ payment, action })}
                    />
                  </div>
                ))}
              {paymentResult && (
                <p
                  className="payment-result-note"
                  style={{
                    margin: '4px 0 0',
                    fontSize: '0.85rem',
                    color: paymentResult.noOp ? '#075985' : '#065f46',
                  }}
                >
                  {paymentResult.text}
                </p>
              )}
            </div>
          )}
          <button className="btn btn-link" onClick={onViewPayment}>
            View Full Payment Details →
          </button>
        </section>

        {/* Internal Notes — admin-only; shown only when the reservation carries them */}
        {reservation.internalNotes && (
          <section className="popup-section">
            <h3 className="section-title">Internal Notes</h3>
            <p className="internal-notes-text">{reservation.internalNotes}</p>
          </section>
        )}

        {/* Payment Link — pending-payment bookings always have an outstanding
            link; confirmed bookings can too (modification adjustments), so
            show it whenever a balance remains. */}
        {(reservation.status === 'pending_payment' ||
          (reservation.status === 'confirmed' && !paymentLoading && reservation.totalCost - totalPaid > 0.5)) && (
          <PaymentLinkSection
            confirmationCode={reservation.confirmationCode}
            guestEmail={member?.email || reservation.guestEmail}
          />
        )}

        {/* Modification History */}
        <ModificationHistorySection
          {...modHistory}
          useClassStyles={true}
          reservation={reservation}
          onPaymentsChanged={loadPayments}
        />

        {/* Member Review — only for completed/cancelled stays with a linked member */}
        {member && (
          <ReservationReviewSection
            reservationId={reservation.id}
            memberId={member.id}
          />
        )}
      </div>

      {pendingAction && (
        <PaymentStatusConfirmModal
          payment={pendingAction.payment}
          action={pendingAction.action}
          onClose={() => setPendingAction(null)}
          onUpdated={(response) => {
            setPendingAction(null);
            setPaymentResult({ text: statusUpdateResultText(response), noOp: response.no_op });
            if (!response.no_op) {
              loadPayments();
            }
          }}
        />
      )}
    </Modal>
  );
};

export default ReservationPopup;
